import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useHttp from '../../hooks/http.hook';
import '../business/Business.css';
import './CreateSoldOrderPage.css';
import CreateBusinessHeader from '../business/components/CreateBusinessHeader';
import CreateFooter from '../business/components/CreateFooter';
import CreateSoldOrderPromotionsSection from './CreateSoldOrderPromotionsSection';
import ProductsSearch from '../product/components/ProductsSearch';
import {
  parseQuantityInput,
  getProductUnitPrice,
  lineTotalWithDiscountPercent,
  countPositiveQtyLines,
  mergeProductCache,
} from './soldOrderPromotionUtils';
import { BusinessTypes } from '../../types/BusinessType';
import RentalPeriodPicker from './components/RentalPeriodPicker';
import {
  formatPeriodRange,
  rentalLineKey,
  rentalLineTotal,
  rentalUnitPrice,
  roundMoney,
  uniquePeriodSuggestions,
} from '../../utils/rentalPeriod';

const SHIPMENT_PRODUCT_ID = -10;

function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0.00';
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function getShipmentUnitPrice(shipment) {
  const raw = shipment?.price;
  const n = typeof raw === 'number' ? raw : parseFloat(String(raw ?? '').replace(',', '.'));
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

function CreateSoldOrderPage() {
  const navigate = useNavigate();
  const { branchId } = useParams();
  const { loading, requestWithMeta } = useHttp();

  const [products, setProducts] = React.useState([]);
  const [submitError, setSubmitError] = React.useState(null);
  const [submitSuccess, setSubmitSuccess] = React.useState(null);
  const [quantityById, setQuantityById] = React.useState({});

  const [productsModalOpen, setProductsModalOpen] = React.useState(false);
  const [modalDraftQtyById, setModalDraftQtyById] = React.useState({});
  const [activeSelectedKey, setActiveSelectedKey] = React.useState(null);

  const [shipments, setShipments] = React.useState([]);
  const [shipmentsLoading, setShipmentsLoading] = React.useState(false);
  const [selectedShipment, setSelectedShipment] = React.useState(null);
  const [shipmentAddress, setShipmentAddress] = React.useState('');
  const [shipmentModalOpen, setShipmentModalOpen] = React.useState(false);
  const [modalDraftShipmentId, setModalDraftShipmentId] = React.useState(null);
  const [customerDetailsOpen, setCustomerDetailsOpen] = React.useState(false);
  const [customerName, setCustomerName] = React.useState('');
  const [customerPhone, setCustomerPhone] = React.useState('');
  const [customerComment, setCustomerComment] = React.useState('');
  const [businessTypeId, setBusinessTypeId] = React.useState(BusinessTypes.STORE);
  const [rentalLines, setRentalLines] = React.useState([]);
  const [rentalPicker, setRentalPicker] = React.useState(null);
  const isRental = businessTypeId === BusinessTypes.RENTAL;

  const [promotionQtyByPromotionId, setPromotionQtyByPromotionId] = React.useState({});
  const [promotionDiscountByProductId, setPromotionDiscountByProductId] = React.useState({});

  const productById = React.useMemo(() => {
    const m = {};
    products.forEach((p) => {
      const id = p?._id ?? p?.id;
      if (id == null || id === '') return;
      m[String(id)] = p;
    });
    return m;
  }, [products]);

  const mergeLoadedProducts = React.useCallback((list) => {
    setProducts((prev) => mergeProductCache(prev, list));
  }, []);

  const handlePromotionDiscountsLoaded = React.useCallback((promotionId, discountByProductId) => {
    setPromotionDiscountByProductId((prev) => ({
      ...prev,
      [promotionId]: discountByProductId,
    }));
  }, []);

  const onCommitPromotionQuantities = React.useCallback((promotionId, draftInner) => {
    setPromotionQtyByPromotionId((prev) => {
      const next = { ...prev };
      const key = String(promotionId);
      const cleaned = {};
      Object.entries(draftInner || {}).forEach(([k, v]) => {
        const q = parseQuantityInput(v);
        if (Number.isFinite(q) && q > 0) cleaned[String(k)] = v;
      });
      if (Object.keys(cleaned).length === 0) delete next[key];
      else next[key] = cleaned;
      return next;
    });
  }, []);

  const fetchShipments = React.useCallback(async () => {
    if (!branchId) return;
    setShipmentsLoading(true);
    try {
      const res = await requestWithMeta(
        `/operator/branches/${encodeURIComponent(branchId)}/shipments`,
        'GET'
      );
      if (!res?.ok) {
        setShipments([]);
        return;
      }
      setShipments(Array.isArray(res?.data?.shipments) ? res.data.shipments : []);
    } catch (_) {
      setShipments([]);
    } finally {
      setShipmentsLoading(false);
    }
  }, [branchId, requestWithMeta]);

  React.useEffect(() => {
    if (!branchId) {
      navigate('/branch/store', { replace: true });
      return;
    }
    fetchShipments();
  }, [branchId, fetchShipments, navigate]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await requestWithMeta('/operator/business', 'GET');
      if (cancelled || !res?.ok) return;
      const business = res?.data?.business ?? res?.data ?? null;
      setBusinessTypeId(Number(business?.typeId) === BusinessTypes.RENTAL
        ? BusinessTypes.RENTAL
        : BusinessTypes.STORE);
    })();
    return () => {
      cancelled = true;
    };
  }, [requestWithMeta]);

  const catalogSelectedCount = React.useMemo(() => {
    if (isRental) return rentalLines.length;
    return Object.values(quantityById).filter((value) => {
      const parsed = parseQuantityInput(value);
      return Number.isFinite(parsed) && parsed > 0;
    }).length;
  }, [isRental, rentalLines.length, quantityById]);

  const promotionSelectedCount = React.useMemo(() => {
    if (isRental) return 0;
    let n = 0;
    Object.values(promotionQtyByPromotionId).forEach((inner) => {
      n += countPositiveQtyLines(inner);
    });
    return n;
  }, [isRental, promotionQtyByPromotionId]);

  const selectedCount = catalogSelectedCount + promotionSelectedCount + (selectedShipment ? 1 : 0);
  const hasProductLines = catalogSelectedCount + promotionSelectedCount > 0;

  const selectedLines = React.useMemo(() => {
    const lines = [];

    if (isRental) {
      rentalLines.forEach((line) => {
        const from = new Date(line.fromAt);
        const to = new Date(line.toAt);
        const unitPrice = rentalUnitPrice(line.unitBase, from, to, line.durationMinutes) ?? 0;
        lines.push({
          key: line.key,
          source: 'rental',
          productId: line.productId,
          promotionId: line.promotionId,
          name: line.name,
          imageUrl: line.imageUrl,
          qty: line.quantity,
          unitPrice,
          lineTotal: rentalLineTotal(line.unitBase, from, to, line.durationMinutes, line.quantity) ?? 0,
          discount: line.discount,
          badge: line.discount ? `−${line.discount}%` : null,
          periodLabel: formatPeriodRange(from, to),
        });
      });
    } else {
    Object.entries(quantityById).forEach(([id, raw]) => {
      const qty = parseQuantityInput(raw);
      if (!Number.isFinite(qty) || qty <= 0) return;
      const product = productById[String(id)];
      const unitPrice = getProductUnitPrice(product);
      lines.push({
        key: `catalog-${id}`,
        source: 'catalog',
        productId: Number.parseInt(String(id), 10) || id,
        promotionId: null,
        name: product?.name || 'Без названия',
        imageUrl: product?.imageUrl || '',
        qty,
        unitPrice,
        lineTotal: qty * unitPrice,
        discount: null,
        badge: null,
      });
    });

    Object.entries(promotionQtyByPromotionId).forEach(([promotionId, inner]) => {
      if (!inner || typeof inner !== 'object') return;
      const discByProd = promotionDiscountByProductId[promotionId] || {};
      Object.entries(inner).forEach(([productId, rawQty]) => {
        const qty = parseQuantityInput(rawQty);
        if (!Number.isFinite(qty) || qty <= 0) return;
        const product = productById[String(productId)];
        const unit = getProductUnitPrice(product);
        const disc = discByProd[String(productId)] ?? 0;
        lines.push({
          key: `promo-${promotionId}-${productId}`,
          source: 'promo',
          productId,
          promotionId,
          name: product?.name || `Товар ${productId}`,
          imageUrl: product?.imageUrl || '',
          qty,
          unitPrice: unit,
          lineTotal: lineTotalWithDiscountPercent(unit, disc, qty),
          discount: disc,
          badge: disc > 0 ? `−${disc}%` : 'Акция',
        });
      });
    });
    }

    if (selectedShipment) {
      const sid = selectedShipment?.id ?? selectedShipment?._id;
      const unitPrice = getShipmentUnitPrice(selectedShipment);
      const address = shipmentAddress.trim();
      lines.push({
        key: `shipment-${sid}`,
        source: 'shipment',
        productId: SHIPMENT_PRODUCT_ID,
        promotionId: null,
        name: selectedShipment?.locationName || 'Доставка',
        imageUrl: '',
        qty: 1,
        unitPrice,
        lineTotal: unitPrice,
        discount: null,
        badge: 'Доставка',
        address: address || null,
      });
    }

    return lines;
  }, [
    quantityById,
    promotionQtyByPromotionId,
    promotionDiscountByProductId,
    productById,
    selectedShipment,
    shipmentAddress,
    isRental,
    rentalLines,
  ]);

  const totalPrice = React.useMemo(() => {
    return selectedLines.reduce((acc, line) => acc + line.lineTotal, 0);
  }, [selectedLines]);

  const skeletonItems = React.useMemo(() => Array.from({ length: 4 }, (_, i) => i), []);

  const shipmentReady = !selectedShipment || shipmentAddress.trim().length > 0;
  const isSubmitDisabled = loading || !hasProductLines || !shipmentReady;

  const modalDraftSelectedCount = countPositiveQtyLines(modalDraftQtyById);
  const selectedShipmentId = selectedShipment?.id ?? selectedShipment?._id ?? null;
  const showShipmentSection = shipmentsLoading || shipments.length > 0;

  function openProductsModal() {
    setModalDraftQtyById({ ...quantityById });
    setProductsModalOpen(true);
  }

  function closeProductsModal() {
    setProductsModalOpen(false);
  }

  function handleApproveProducts() {
    const next = {};
    Object.entries(modalDraftQtyById).forEach(([id, raw]) => {
      const q = parseQuantityInput(raw);
      if (Number.isFinite(q) && q > 0) next[id] = raw;
    });
    setQuantityById(next);
    closeProductsModal();
  }

  function openShipmentModal() {
    setModalDraftShipmentId(selectedShipmentId != null ? String(selectedShipmentId) : null);
    setShipmentModalOpen(true);
  }

  function closeShipmentModal() {
    setShipmentModalOpen(false);
  }

  function handleApproveShipment() {
    if (modalDraftShipmentId == null) {
      setSelectedShipment(null);
      setShipmentAddress('');
    } else {
      const next = shipments.find(
        (s) => String(s?.id ?? s?._id) === String(modalDraftShipmentId)
      );
      setSelectedShipment(next || null);
      if (!next) setShipmentAddress('');
    }
    closeShipmentModal();
  }

  function handleRemoveSelectedLine(line) {
    if (!line) return;
    if (line.source === 'rental') {
      setRentalLines((prev) => prev.filter((item) => item.key !== line.key));
    } else if (line.source === 'catalog') {
      setQuantityById((prev) => {
        const next = { ...prev };
        next[line.productId] = '';
        if (next[String(line.productId)] !== undefined) next[String(line.productId)] = '';
        return next;
      });
    } else if (line.source === 'promo' && line.promotionId != null) {
      setPromotionQtyByPromotionId((prev) => {
        const promoKey = String(line.promotionId);
        const inner = { ...(prev[promoKey] || {}) };
        delete inner[String(line.productId)];
        delete inner[line.productId];
        const next = { ...prev };
        if (Object.keys(inner).length === 0) delete next[promoKey];
        else next[promoKey] = inner;
        return next;
      });
    } else if (line.source === 'shipment') {
      setSelectedShipment(null);
      setShipmentAddress('');
    }
    setActiveSelectedKey(null);
  }

  async function handleSubmit() {
    setSubmitError(null);
    setSubmitSuccess(null);

    const catalogLines = isRental
      ? rentalLines.map((line) => ({
        productId: line.productId,
        quantity: line.quantity,
        promotionId: line.promotionId,
        fromAt: line.fromAt,
        toAt: line.toAt,
      }))
      : Object.entries(quantityById)
      .map(([id, raw]) => {
        const parsed = parseQuantityInput(raw);
        if (!Number.isFinite(parsed) || parsed <= 0) return null;
        const productId = Number.parseInt(String(id), 10);
        if (!Number.isFinite(productId)) return null;
        return { productId, quantity: parsed, promotionId: null };
      })
      .filter(Boolean);

    const promoLines = [];
    if (!isRental) {
    Object.entries(promotionQtyByPromotionId).forEach(([promotionId, inner]) => {
      if (!inner || typeof inner !== 'object') return;
      Object.entries(inner).forEach(([productId, raw]) => {
        const parsed = parseQuantityInput(raw);
        if (!Number.isFinite(parsed) || parsed <= 0) return;
        promoLines.push({
          productId,
          quantity: parsed,
          promotionId,
        });
      });
    });
    }

    const selectedPayload = [...catalogLines, ...promoLines];

    if (selectedPayload.length === 0) {
      setSubmitError('Укажите количество больше 0 хотя бы для одного товара');
      return;
    }

    if (selectedShipment && !shipmentAddress.trim()) {
      setSubmitError('Укажите адрес доставки');
      return;
    }

    const body = {
      products: selectedPayload,
    };
    const trimmedName = customerName.trim();
    const trimmedPhone = customerPhone.trim();
    const trimmedComment = customerComment.trim();
    if (trimmedName) body.customerName = trimmedName;
    if (trimmedPhone) body.customerPhone = trimmedPhone;
    if (trimmedComment) body.customerDescription = trimmedComment;
    if (selectedShipment) {
      body.shipmentId = selectedShipment.id ?? selectedShipment._id;
      body.shipmentAddress = shipmentAddress.trim();
    }

    const res = await requestWithMeta(`/operator/branches/${branchId}/orders`, 'POST', body);

    if (!res?.ok) {
      setSubmitError(res?.data?.error || res?.data?.message || 'Не удалось создать заказ');
      return;
    }

    setSubmitSuccess('Заказ успешно создан');
    navigate(`/branch/${branchId}/orders`, { replace: true });
  }

  return (
    <div className="shop-create-root create-sold-order-root">
      <div className="shop-create-mobile">
        <CreateBusinessHeader
          title="Создать продажу"
          subtitle="Укажите количество проданных товаров"
          onBack={() => navigate(`/branch/${branchId}/orders`)}
        />

        <main className="shop-create-content">
          {submitError ? (
            <section className="shop-create-section shop-create-sectionError">
              <div className="shop-create-label">Ошибка</div>
              <div className="shop-create-help shop-create-helpTight">{submitError}</div>
            </section>
          ) : null}

          {submitSuccess ? (
            <section className="shop-create-section create-sold-order-sectionSuccess">
              <div className="shop-create-label">Готово</div>
              <div className="shop-create-help shop-create-helpTight">{submitSuccess}</div>
            </section>
          ) : null}

          <div className="create-sold-order-split">
          <section className="shop-create-section create-sold-order-split-selected">

            {selectedLines.length > 0 ? (
              <div className="create-sold-order-selectedList" aria-label="Выбранные товары">
                {selectedLines.map((line) => {
                  const isActive = activeSelectedKey === line.key;
                  const item = (
                    <div
                      className={`create-sold-order-selectedItem${isActive ? ' is-active' : ''}`}
                      role="button"
                      tabIndex={0}
                      aria-pressed={isActive}
                      onClick={() => {
                        setActiveSelectedKey((prev) => (prev === line.key ? null : line.key));
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setActiveSelectedKey((prev) => (prev === line.key ? null : line.key));
                        }
                      }}
                    >
                      {line.imageUrl ? (
                        <img
                          className="create-sold-order-selectedImage"
                          src={line.imageUrl}
                          alt=""
                          loading="lazy"
                        />
                      ) : (
                        <div className="create-sold-order-selectedImage create-sold-order-selectedImage--placeholder" />
                      )}
                      <div className="create-sold-order-selectedInfo">
                        <div className="create-sold-order-selectedName">{line.name}</div>
                        <div className="create-sold-order-selectedMeta">
                          {line.periodLabel ? <div>{line.periodLabel}</div> : null}
                          {formatMoney(line.unitPrice)} сом × {line.qty}
                          {line.badge ? (
                            <span className="create-sold-order-selectedBadge">{line.badge}</span>
                          ) : null}
                        </div>
                      </div>
                      <div className="create-sold-order-selectedRight">
                        <div className="create-sold-order-selectedTotal">
                          {formatMoney(line.lineTotal)} сом
                        </div>
                        {isActive ? (
                          <button
                            type="button"
                            className="create-sold-order-selectedDelete"
                            aria-label={`Удалить ${line.name}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveSelectedLine(line);
                            }}
                          >
                            <svg
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              aria-hidden="true"
                            >
                              <path
                                d="M3 6h18"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                              />
                              <path
                                d="M8 6V4.5A1.5 1.5 0 0 1 9.5 3h5A1.5 1.5 0 0 1 16 4.5V6"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                              />
                              <path
                                d="M19 6l-.7 12.1A2 2 0 0 1 16.3 20H7.7a2 2 0 0 1-2-1.9L5 6"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M10 11v5M14 11v5"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                              />
                            </svg>
                          </button>
                        ) : null}
                      </div>
                    </div>
                  );

                  if (line.source === 'shipment') {
                    return (
                      <div key={line.key} className="create-sold-order-selectedShipmentWrap">
                        {item}
                        <label className="create-sold-order-shipmentAddress">
                          <span className="create-sold-order-shipmentAddressLabel">Адрес доставки</span>
                          <input
                            type="text"
                            className="create-sold-order-shipmentAddressInput"
                            value={shipmentAddress}
                            onChange={(e) => setShipmentAddress(e.target.value)}
                            placeholder="Улица, дом, квартира"
                            autoComplete="street-address"
                          />
                        </label>
                      </div>
                    );
                  }

                  return (
                    <React.Fragment key={line.key}>
                      {item}
                    </React.Fragment>
                  );
                })}
              </div>
            ) : (
              <div className="create-sold-order-selectedEmpty">
                Пока ничего не выбрано. Откройте товары или акцию.
              </div>
            )}

            <div className="create-sold-order-listHeader create-sold-order-listHeader--tight">
              <div className="shop-create-label create-sold-order-listTitle create-sold-order-listTitle--lg">
                Итого: {formatMoney(totalPrice)} сом
              </div>
              <div className="shop-create-help shop-create-helpTight create-sold-order-listHeaderHelp">
                {selectedCount > 0 ? `${selectedCount} позиций` : '0 позиций'}
              </div>
            </div>

          </section>

          <div className="create-sold-order-split-side">
          <section className="shop-create-section create-sold-order-split-customer">
            <div className="create-sold-order-customerDetails">
              <button
                type="button"
                className={`create-sold-order-productsOpenBtn${customerDetailsOpen ? ' is-open' : ''}`}
                aria-expanded={customerDetailsOpen}
                onClick={() => setCustomerDetailsOpen((open) => !open)}
              >
                <div className="create-sold-order-productsOpenBtnText">
                  <span className="create-sold-order-productsOpenBtnTitle">Данные клиента</span>
                  <span className="create-sold-order-productsOpenBtnHint">
                    {[customerName, customerPhone, customerComment].some((value) => String(value).trim())
                      ? 'Заполнено'
                      : 'Необязательно'}
                  </span>
                </div>
                <span className="create-sold-order-productsOpenBtnChevron" aria-hidden>
                  ›
                </span>
              </button>

              {customerDetailsOpen ? (
                <div className="create-sold-order-customerDetailsBody">
                  <label className="shop-create-label" htmlFor="sold-order-customer-name">
                    Имя
                  </label>
                  <input
                    id="sold-order-customer-name"
                    className="shop-create-input"
                    type="text"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="Имя клиента"
                    autoComplete="name"
                  />

                  <label className="shop-create-label" htmlFor="sold-order-customer-phone">
                    Телефон
                  </label>
                  <input
                    id="sold-order-customer-phone"
                    className="shop-create-input"
                    type="tel"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    placeholder="Номер телефона"
                    autoComplete="tel"
                    inputMode="tel"
                  />

                  <label className="shop-create-label" htmlFor="sold-order-customer-comment">
                    Комментарий
                  </label>
                  <textarea
                    id="sold-order-customer-comment"
                    className="shop-create-input create-sold-order-customerComment"
                    value={customerComment}
                    onChange={(e) => setCustomerComment(e.target.value)}
                    placeholder="Комментарий к заказу"
                    rows={3}
                  />

                </div>
              ) : null}
            </div>
          </section>


          <section className="shop-create-section create-sold-order-split-products">

            <button
              type="button"
              className="create-sold-order-productsOpenBtn"
              onClick={openProductsModal}
            >
              <div className="create-sold-order-productsOpenBtnText">
                <span className="create-sold-order-productsOpenBtnTitle">Товары</span>
                <span className="create-sold-order-productsOpenBtnHint">
                  {catalogSelectedCount > 0
                    ? `Выбрано: ${catalogSelectedCount}`
                    : 'Поиск и выбор из каталога'}
                </span>
              </div>
              <span className="create-sold-order-productsOpenBtnChevron" aria-hidden>
                ›
              </span>
            </button>

            <CreateSoldOrderPromotionsSection
              branchId={branchId}
              requestWithMeta={requestWithMeta}
              productById={productById}
              promotionQtyByPromotionId={promotionQtyByPromotionId}
              onCommitPromotionQuantities={onCommitPromotionQuantities}
              onPromotionDiscountsLoaded={handlePromotionDiscountsLoaded}
              onProductsLoaded={mergeLoadedProducts}
              isRental={isRental}
              onPickRental={(row, promotionId) => {
                const product = row?.product;
                if (!product?.durationMinutes) {
                  setSubmitError('У товара не задана длительность аренды');
                  return false;
                }
                setSubmitError(null);
                setRentalPicker({
                  product: { ...product, name: row.name || product.name },
                  promotionId,
                  discount: row.discount,
                });
              }}
            />

            {showShipmentSection ? (
              <section className="create-sold-order-shipmentSection">
                {/* <div className="shop-create-label" style={{ marginBottom: 5, marginTop: 8 }}>
                  Доставка
                </div> */}
                <button
                  type="button"
                  className="create-sold-order-productsOpenBtn"
                  onClick={openShipmentModal}
                  disabled={shipmentsLoading || shipments.length === 0}
                >
                  <div className="create-sold-order-productsOpenBtnText">
                    <span className="create-sold-order-productsOpenBtnTitle">
                      {selectedShipment
                        ? (selectedShipment.locationName || 'Доставка')
                        : 'Выбрать доставку'}
                    </span>
                    <span className="create-sold-order-productsOpenBtnHint">
                      {shipmentsLoading
                        ? 'Загрузка…'
                        : selectedShipment
                          ? `${formatMoney(getShipmentUnitPrice(selectedShipment))} сом`
                          : `${shipments.length} зон`}
                    </span>
                  </div>
                  <span className="create-sold-order-productsOpenBtnChevron" aria-hidden>
                    ›
                  </span>
                </button>
              </section>
            ) : null}
          </section>
          </div>
          </div>
        </main>

        <CreateFooter
          disabled={isSubmitDisabled}
          label={loading ? 'Отправка…' : 'Создать продажу'}
          onCreate={handleSubmit}
        />
      </div>

      {productsModalOpen ? (
        <div
          className="create-sold-order-promoModal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="create-sold-order-products-modal-title"
          onClick={closeProductsModal}
        >
          <div
            className="create-sold-order-promoModal-sheet"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="create-sold-order-promoModal-header">
              <div>
                <h2
                  id="create-sold-order-products-modal-title"
                  className="create-sold-order-promoModal-title"
                >
                  Товары
                </h2>
                <p className="create-sold-order-promoModal-sub">
                  Укажите количество
                </p>
              </div>
              <button
                type="button"
                className="create-sold-order-promoModal-close"
                onClick={closeProductsModal}
                aria-label="Закрыть без сохранения"
              >
                ×
              </button>
            </div>

            <div className="create-sold-order-promoModal-body">
              <ProductsSearch
                flatten
                branchId={branchId}
                enabled={productsModalOpen}
                tone="sheet"
                emptyText="Товары не найдены."
                onProductsLoaded={mergeLoadedProducts}
              >
                {({ products: pageProducts, loading: pageLoading }) => (
                  pageLoading && pageProducts.length === 0 ? (
                    skeletonItems.map((item) => (
                      <div key={item} className="create-sold-order-item create-sold-order-item--skeleton">
                        <div className="shop-create-skeleton create-sold-order-skeletonImage" />
                        <div className="shop-create-skeleton shop-create-skeleton--text create-sold-order-skeletonName" />
                        <div className="shop-create-skeleton create-sold-order-skeletonInput" />
                      </div>
                    ))
                  ) : (
                    <div className="create-sold-order-promoModal-list">
                      {pageProducts.map((product) => {
                        const productId = product?._id ?? product?.id;
                        const inputQty =
                          modalDraftQtyById[productId] ?? modalDraftQtyById[String(productId)] ?? '';
                        const stockQty = product?.quantity ?? 0;

                        return (
                          <label key={productId} className="create-sold-order-item">
                            <img
                              className="create-sold-order-image"
                              src={product?.imageUrl || ''}
                              alt={product?.name || 'product'}
                              loading="lazy"
                            />

                            <div className="create-sold-order-info">
                              <div className="create-sold-order-name">
                                {product?.name || 'Без названия'}
                              </div>
                              <div className="create-sold-order-stock">В наличии: {stockQty}</div>
                            <div className="create-sold-order-stock">
                              {isRental && product?.durationMinutes
                                ? `${formatMoney(getProductUnitPrice(product))} сом / период`
                                : `${formatMoney(getProductUnitPrice(product))} сом`}
                            </div>
                          </div>

                          {isRental ? (
                            <button
                              type="button"
                              className="create-sold-order-qtyInput"
                              onClick={() => {
                                if (!product?.durationMinutes) {
                                  setSubmitError('У товара не задана длительность аренды');
                                  return;
                                }
                                setSubmitError(null);
                                setRentalPicker({ product, promotionId: null, discount: null });
                              }}
                            >
                              Период
                            </button>
                          ) : (
                          <input
                            type="number"
                            min="0"
                            max={stockQty}
                            step="1"
                            inputMode="numeric"
                            className="create-sold-order-qtyInput"
                            value={inputQty}
                            onChange={(e) => {
                              const nextValue = e.target.value;
                              setModalDraftQtyById((prev) => ({
                                ...prev,
                                [productId]: nextValue,
                              }));
                            }}
                            placeholder="0"
                            aria-label={`Количество для ${product?.name || 'товара'}`}
                          />
                          )}
                          </label>
                        );
                      })}
                    </div>
                  )
                )}
              </ProductsSearch>
            </div>

            <div className="create-sold-order-promoModal-footer">
              <div className="create-sold-order-promoModal-footerSummary">
                Выбрано: {modalDraftSelectedCount}
              </div>
              <button
                type="button"
                className="create-sold-order-promoModal-approve"
                onClick={handleApproveProducts}
              >
                Подтвердить
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {shipmentModalOpen ? (
        <div
          className="create-sold-order-promoModal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="create-sold-order-shipment-modal-title"
          onClick={closeShipmentModal}
        >
          <div
            className="create-sold-order-promoModal-sheet"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="create-sold-order-promoModal-header">
              <div>
                <h2
                  id="create-sold-order-shipment-modal-title"
                  className="create-sold-order-promoModal-title"
                >
                  Доставка
                </h2>
                <p className="create-sold-order-promoModal-sub">
                  Выберите зону доставки
                </p>
              </div>
              <button
                type="button"
                className="create-sold-order-promoModal-close"
                onClick={closeShipmentModal}
                aria-label="Закрыть без сохранения"
              >
                ×
              </button>
            </div>

            <div className="create-sold-order-promoModal-body">
              {shipmentsLoading ? (
                skeletonItems.map((item) => (
                  <div key={item} className="create-sold-order-item create-sold-order-item--skeleton">
                    <div className="shop-create-skeleton shop-create-skeleton--text create-sold-order-skeletonName" />
                    <div className="shop-create-skeleton create-sold-order-skeletonInput" />
                  </div>
                ))
              ) : shipments.length === 0 ? (
                <div className="shop-create-help">Нет доступных зон доставки.</div>
              ) : (
                <div className="create-sold-order-promoModal-list">
                  <button
                    type="button"
                    className={`create-sold-order-shipmentOption${modalDraftShipmentId == null ? ' is-selected' : ''}`}
                    onClick={() => setModalDraftShipmentId(null)}
                    aria-pressed={modalDraftShipmentId == null}
                  >
                    <span className="create-sold-order-shipmentOptionText">
                      <span className="create-sold-order-shipmentOptionName">Без доставки</span>
                      <span className="create-sold-order-shipmentOptionPrice">Самовывоз</span>
                    </span>
                    <span
                      className={`create-sold-order-shipmentRadio${modalDraftShipmentId == null ? ' is-checked' : ''}`}
                      aria-hidden
                    />
                  </button>
                  {shipments.map((shipment) => {
                    const id = shipment?.id ?? shipment?._id;
                    const selected = String(id) === String(modalDraftShipmentId);
                    return (
                      <button
                        key={String(id)}
                        type="button"
                        className={`create-sold-order-shipmentOption${selected ? ' is-selected' : ''}`}
                        onClick={() => setModalDraftShipmentId(String(id))}
                        aria-pressed={selected}
                      >
                        <span className="create-sold-order-shipmentOptionText">
                          <span className="create-sold-order-shipmentOptionName">
                            {shipment?.locationName || `Доставка #${id}`}
                          </span>
                          <span className="create-sold-order-shipmentOptionPrice">
                            {formatMoney(getShipmentUnitPrice(shipment))} сом
                          </span>
                        </span>
                        <span
                          className={`create-sold-order-shipmentRadio${selected ? ' is-checked' : ''}`}
                          aria-hidden
                        />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="create-sold-order-promoModal-footer">
              <div className="create-sold-order-promoModal-footerSummary">
                {modalDraftShipmentId == null
                  ? 'Доставка не выбрана'
                  : 'Зона выбрана'}
              </div>
              <button
                type="button"
                className="create-sold-order-promoModal-approve"
                onClick={handleApproveShipment}
                disabled={shipmentsLoading}
              >
                Подтвердить
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {rentalPicker ? (
        <RentalPeriodPicker
          key={rentalPicker.product?._id ?? rentalPicker.product?.id}
          productName={rentalPicker.product?.name}
          unitPrice={roundMoney(
            getProductUnitPrice(rentalPicker.product)
            * (1 - (Number(rentalPicker.discount) || 0) / 100)
          ) ?? 0}
          durationMinutes={rentalPicker.product?.durationMinutes}
          suggestions={uniquePeriodSuggestions(rentalLines.map((line) => ({
            ...line,
            id: line.productId,
          })))}
          allowPast
          loadAvailable={async (fromAt, toAt) => {
            const productId = rentalPicker.product?._id ?? rentalPicker.product?.id;
            const params = new URLSearchParams({
              productId: String(productId),
              fromAt,
              toAt,
            });
            const res = await requestWithMeta(
              `/operator/branches/${encodeURIComponent(branchId)}/rental-availability?${params.toString()}`
            );
            if (!res?.ok) throw new Error(res?.data?.error || 'Не удалось проверить наличие');
            return res?.data?.available;
          }}
          loadOccupancy={async (fromAt, toAt) => {
            const productId = rentalPicker.product?._id ?? rentalPicker.product?.id;
            const params = new URLSearchParams({
              productId: String(productId),
              fromAt,
              toAt,
            });
            const res = await requestWithMeta(
              `/operator/branches/${encodeURIComponent(branchId)}/rental-occupancy?${params.toString()}`
            );
            if (!res?.ok) throw new Error(res?.data?.error || 'Не удалось проверить наличие');
            return res?.data?.steps;
          }}
          onConfirm={(period) => {
            const product = rentalPicker.product;
            const productId = product?._id ?? product?.id;
            const key = `${rentalPicker.promotionId || 'catalog'}|${rentalLineKey(productId, period.fromAt, period.toAt)}`;
            const unitBase = roundMoney(
              getProductUnitPrice(product) * (1 - (Number(rentalPicker.discount) || 0) / 100)
            ) ?? 0;
            setRentalLines((prev) => {
              const existing = prev.find((line) => line.key === key);
              if (existing) {
                return prev.map((line) => (
                  line.key === key
                    ? { ...line, quantity: line.quantity + period.quantity }
                    : line
                ));
              }
              return [...prev, {
                key,
                productId,
                name: product?.name || 'Товар',
                imageUrl: product?.imageUrl || '',
                unitBase,
                durationMinutes: Number(product?.durationMinutes),
                fromAt: period.fromAt,
                toAt: period.toAt,
                quantity: period.quantity,
                promotionId: rentalPicker.promotionId,
                discount: rentalPicker.discount,
              }];
            });
            setRentalPicker(null);
            setProductsModalOpen(false);
          }}
          onClose={() => setRentalPicker(null)}
        />
      ) : null}
    </div>
  );
}

export default CreateSoldOrderPage;
